import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import ConfirmModal from '../ConfirmModal';
import { MonoLabel, SectionCap, EditorialButton, EmptyState } from '../Editorial';
import { beaconOpened, beaconRowOnce } from '../../lib/recruitingBeacons';
import CollegePicker from '../CollegePicker';

/* Visits half of the old OffersPage. Self-contained: own fetch, own state, own
   confirm modal and toast, no props. Behaviour is unchanged from the page it
   came from — including the engagement beacons and the 20-visit cap message.

   Row keys are prefixed `v` so visit slot 1 and offer slot 1 stay distinct in
   the autocomplete's query map. */

const VISIT_TYPES = [
  { value: 'official', label: 'Official' },
  { value: 'unofficial', label: 'Unofficial' },
];

const VISIT_FORMATS = [
  { value: 'game_day', label: 'Home game' },
  { value: 'junior_day', label: 'Group event' },
  { value: 'midweek', label: 'In school' },
  { value: 'prospect_camp', label: 'On-field eval' },
  { value: 'overnight', label: 'Stay with team' },
  { value: 'virtual', label: 'Remote tour' },
];

const rowKey = (slot) => `v${slot}`;

export default function VisitsPanel() {
  const { API_URL } = useAuth();
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [visits, setVisits] = useState([]);
  const [confirmAction, setConfirmAction] = useState(null);
  const beaconedRows = useRef(new Set());

  const getAxiosConfig = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  });

  const toast = (text, type = '') => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => setMessage(''), 3000);
  };

  useEffect(() => {
    fetchVisits();
  }, []);

  const emptyVisit = (slot) => ({
    slot_number: slot,
    college_name: '',
    visit_date: '',
    visit_end_date: '',
    visit_type: '',
    visit_format: 'game_day',
  });

  const fetchVisits = async () => {
    try {
      const res = await axios.get(`${API_URL}/athletes/visits`, getAxiosConfig());
      const data = res.data || [];
      // Always show at least one empty form, as the original page did — an
      // athlete with no visits gets a row to fill in, not just an empty state.
      setVisits(data.length === 0 ? [emptyVisit(1)] : data);
    } catch (err) {
      console.error('Error fetching visits:', err);
    }
  };

  const saveVisit = async (visit) => {
    try {
      await axios.post(`${API_URL}/athletes/visits`, visit, getAxiosConfig());
      toast('Saved');
    } catch (err) {
      console.error('Error saving visit:', err);
    }
  };

  const handleAddVisit = () => {
    const usedSlots = visits.map(v => v.slot_number);
    let nextSlot = 1;
    while (usedSlots.includes(nextSlot) && nextSlot <= 20) nextSlot++;
    if (nextSlot > 20) {
      toast("You've reached the 20-visit maximum.", 'error');
      return;
    }

    beaconOpened(API_URL, 'visits');
    setVisits([...visits, emptyVisit(nextSlot)]);
  };

  const handleDeleteVisit = (visit) => {
    setConfirmAction({
      title: 'Delete Visit',
      message: `Remove the visit to ${visit.college_name || 'this college'}?`,
      confirmLabel: 'Delete',
      danger: true,
      onConfirm: async () => {
        setConfirmAction(null);
        if (visit.id) {
          try {
            await axios.delete(`${API_URL}/athletes/visits/${visit.id}`, getAxiosConfig());
          } catch (err) {
            console.error('Error deleting visit:', err);
          }
        }
        setVisits(prev => prev.filter(v => v.slot_number !== visit.slot_number));
        toast('Visit deleted');
      },
      onCancel: () => setConfirmAction(null),
    });
  };

  const updateVisitField = (slot, field, value) => {
    beaconRowOnce(beaconedRows, API_URL, 'visits', slot);
    setVisits(prev => prev.map(v =>
      v.slot_number === slot ? { ...v, [field]: value } : v
    ));
  };

  const pickCollege = (slot) => (college) => {
    updateVisitField(slot, 'unitid', college.u);
    updateVisitField(slot, 'college_name', college.n);

    const visit = visits.find(v => v.slot_number === slot);
    if (visit) {
      saveVisit({ ...visit, unitid: college.u, college_name: college.n });
    }
  };

  // Typing replaces any earlier pick, so the old college's id is dropped with
  // the first keystroke. Nothing is saved yet.
  const typeCollege = (slot, text) => setVisits(prev => prev.map(v => (v.slot_number === slot
    ? { ...v, college_name: text, unitid: null } : v)));

  // Free text, once, when the box is left. Explicit `unitid: null` clears the
  // id server-side; an absent key would keep it.
  const commitTypedName = (visit) => (text) => {
    if (text) saveVisit({ ...visit, college_name: text, unitid: null });
  };

  return (
    <>
      {message && (
        <div className={`settings-toast ${messageType === 'error' ? 'settings-toast--error' : ''}`} role="alert">{message}</div>
      )}

      <SectionCap label="Visits" hint="MAX 20" />

      {visits.map((visit) => {
        const key = rowKey(visit.slot_number);
        return (
          <div className="ed-card" key={key}>
            <div className="ed-row ed-row--between" style={{ marginBottom: 8 }}>
              <MonoLabel>VISIT {visit.slot_number}</MonoLabel>
              <EditorialButton variant="ghost" onClick={() => handleDeleteVisit(visit)} style={{ color: 'var(--ed-accent-red)', padding: '6px 12px' }}>
                Delete
              </EditorialButton>
            </div>

            <div className="form-field" style={{ position: 'relative' }}>
              <label className="form-field__label">College / University</label>
              <CollegePicker
                value={visit.college_name}
                onInputChange={(text) => typeCollege(visit.slot_number, text)}
                onSelect={pickCollege(visit.slot_number)}
                onCommitText={commitTypedName(visit)}
              />
            </div>

            <div className="form-row">
              <div className="form-field">
                <label className="form-field__label">Visit Date</label>
                <input
                  type="date"
                  value={visit.visit_date || ''}
                  onChange={(e) => {
                    updateVisitField(visit.slot_number, 'visit_date', e.target.value);
                    if (visit.college_name) saveVisit({ ...visit, visit_date: e.target.value });
                  }}
                  className="form-field__input"
                />
              </div>
              <div className="form-field">
                <label className="form-field__label">Visit Type</label>
                <select
                  value={visit.visit_type || ''}
                  onChange={(e) => {
                    updateVisitField(visit.slot_number, 'visit_type', e.target.value);
                    if (visit.college_name) saveVisit({ ...visit, visit_type: e.target.value });
                  }}
                  className="form-field__select"
                >
                  <option value="">Select...</option>
                  {VISIT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
            </div>

            <div className="form-row">
              {visit.visit_type === 'official' && (
                <div className="form-field">
                  <label className="form-field__label">Visit End Date</label>
                  <input
                    type="date"
                    value={visit.visit_end_date || ''}
                    onChange={(e) => {
                      updateVisitField(visit.slot_number, 'visit_end_date', e.target.value);
                      if (visit.college_name) saveVisit({ ...visit, visit_end_date: e.target.value });
                    }}
                    className="form-field__input"
                  />
                </div>
              )}
              <div className="form-field">
                <label className="form-field__label">Visit Format</label>
                <select
                  value={visit.visit_format || 'game_day'}
                  onChange={(e) => {
                    updateVisitField(visit.slot_number, 'visit_format', e.target.value);
                    if (visit.college_name) saveVisit({ ...visit, visit_format: e.target.value });
                  }}
                  className="form-field__select"
                >
                  {VISIT_FORMATS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                </select>
              </div>
            </div>
          </div>
        );
      })}

      {visits.length === 0 && (
        <EmptyState title="No visits yet." body="Add a visit to track when you've toured a campus or attended an event." />
      )}
      {visits.length < 20 && (
        <EditorialButton variant="ghost" onClick={handleAddVisit}>
          + Add another visit
        </EditorialButton>
      )}

      {confirmAction && (
        <ConfirmModal
          title={confirmAction.title}
          message={confirmAction.message}
          confirmLabel={confirmAction.confirmLabel}
          danger={confirmAction.danger}
          onConfirm={confirmAction.onConfirm}
          onCancel={confirmAction.onCancel || (() => setConfirmAction(null))}
        />
      )}
    </>
  );
}
